import { Component, Input, OnInit, inject } from '@angular/core';
import { GenerationProgress, ReportConfig, ReportTemplate } from '@modules/report-generator/interfaces';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { MatDialogRef } from '@angular/material/dialog';
import { GenerateReportDialogComponent } from '../../shared/generate-report-dialog/generate-report-dialog.component';
import { ReportGeneratorService } from '@modules/report-generator/report-generator.service';
import { Observable, map } from 'rxjs';
import { ReportGeneratorStore } from '@modules/report-generator/report-generator.store';
import { SaveTemplateDialogComponent } from '../../shared/save-template-dialog/save-template-dialog.component';
import { SaveTemplateFormData } from '@shared/interfaces/save-template-dialog.interface';
import { SessionService } from '@core/services/session.service';
import { ReportTemplate as OldReportTemplate } from '@core/services/report-template/report-template.service';
import { Router } from '@angular/router';

@Component({
  selector: 'app-report-header',
  templateUrl: './report-header.component.html',
  styleUrls: ['./report-header.component.scss'],
})
export class ReportHeaderComponent implements OnInit {
  private readonly dialog = inject(MatDialog);
  private readonly reportGeneratorService = inject(ReportGeneratorService);
  private readonly store = inject(ReportGeneratorStore);
  private readonly sessionService = inject(SessionService);
  private readonly router = inject(Router);

  showOldReportGenerator = false;

  progress$ = this.reportGeneratorService.progress$;
  config$: Observable<ReportConfig | null>;
  reportStatus$ = this.reportGeneratorService.progress$.pipe(map((progress) => progress.status));

  @Input() generatePdf!: () => void;
  @Input() configurePdf!: () => void;
  @Input() oldTemplates?: Array<OldReportTemplate>;
  @Input() isConfiguring: boolean;
  public selectedTemplate: OldReportTemplate;

  ngOnInit(): void {
    this.showOldReportGenerator = this.shouldShowOldReportGenerator();
    this.setupListeners();
  }

  getReportTitle(config: ReportConfig): string {
    return config?.template?.name ?? 'PDF Report Designer';
  }

  configureReportText(): string {
    return this.isConfiguring ? 'Preview Report' : 'Configure Report';
  }

  generateReportDisabled(config: ReportConfig): boolean {
    return Boolean(!config?.test);
  }

  getGenerateReportText(config: ReportConfig, progress: GenerationProgress): string {
    if (!config?.test) {
      return 'Loading test...';
    }

    if (progress.status !== 'processing') {
      return 'Generate PDF';
    }
    return `Generating ${(progress.pageTitle ?? '').toLocaleLowerCase()}...`;
  }

  generatePdfReport(config: ReportConfig, status: string): void {
    const hasChanges = config.changes && Array.isArray(config.changes) && config.changes.length > 0;
    if (hasChanges) {
      const dialogRef: MatDialogRef<SaveTemplateDialogComponent> = this.dialog.open(SaveTemplateDialogComponent, {
        width: '480px',
        panelClass: 'general-dialog',
        autoFocus: true,
        disableClose: true,
        data: {
          templateName: config.template?.name!,
          isGlobal: !config.template?.organization,
          hasChanges,
        },
      });

      dialogRef.afterClosed().subscribe((result: SaveTemplateFormData) => {
        if (!result) {
          return;
        }

        const newTemplate = {
          ...config.template!,
          name: result.templateName,
        };

        if (!result.shouldContinue) {
          this.store.saveTemplate(newTemplate, result.shouldClone);
        }

        this.dialog.open(GenerateReportDialogComponent, {
          width: '480px',
          panelClass: 'general-dialog',
          autoFocus: true,
          disableClose: false,
          data: {},
        });

        if (status !== 'processing') {
          this.generatePdf();
        }
      });
    } else {
      this.dialog.open(GenerateReportDialogComponent, {
        width: '480px',
        panelClass: 'general-dialog',
        autoFocus: true,
        disableClose: false,
        data: {},
      });

      if (status !== 'processing') {
        this.generatePdf();
      }
    }
  }

  generateLegacyReport(config: ReportConfig) {
    const testId = config.test?.id;
    if (testId == null) {
      return;
    }
    const athleteId = config.test?.athlete?.id;
    if (athleteId == null) {
      return;
    }

    const url: string = this.router
      .createUrlTree(['/report-preview/' + testId], {
        queryParams: {
          templateId: this.selectedTemplate.id,
          athleteId: athleteId,
        },
      })
      .toString();

    window.open(url, '_blank');
  }

  hasChanges(config: ReportConfig): boolean {
    return (config?.changes ?? []).length > 0;
  }

  private setupListeners(): void {
    this.config$ = this.store.config$;
  }

  private shouldShowOldReportGenerator() {
    return false;
    // const auth: Record<string, any> = JSON.parse(this.sessionService.getCookie('auth') || '{}');
    // const date_joined = auth.date_joined;
    // return date_joined != null ? date_joined < '2026-04-01T00:00:00Z' : false;
  }
}
